import React from "react";

import { Divider, Grid } from "@mui/material";
import { AppListItem } from "Components/AppListItem";
import Checkbox from "Components/Form/Checkbox";
import { TooltipPlacements } from "Components/Form/ToolTipInfo";
import { Fleet } from "Models/fleet";
import { useTranslation } from "react-i18next";
import { formatSimpleDate } from "Util/formatDateAndTime";
import { getMeasurement } from "Util/getMeasurement";

import { EMPTY_CHARACTER } from "../../../../../../../../../constants";
import { Container, Title } from "./styled";
import { formatDateWithUserPref } from "Util/dateFormaterPreference";
import { useAppState } from "Context/StateContext";

interface DetailsSectionProps {
  fleet: Fleet;
}

export const DetailsSection = (props: DetailsSectionProps) => {
  const { fleet } = props;
  const { t: translate } = useTranslation();
  const measurement = getMeasurement();
  const { currentUser } = useAppState();

  return (
    <Container>
      <Title>{translate("details")}</Title>

      <Grid container>
        <Grid item xs={3}>
          <AppListItem
            title={translate("type")}
            subtitle={fleet.equipmentType ?? EMPTY_CHARACTER}
            disableGutters
          />
        </Grid>

        <Grid item xs={3}>
          <AppListItem
            title={translate("mode")}
            subtitle={fleet.typeMode ?? EMPTY_CHARACTER}
            disableGutters
          />
        </Grid>

        <Grid item xs={3}>
          <AppListItem
            title={translate("description")}
            subtitle={fleet.description ?? EMPTY_CHARACTER}
            disableGutters
          />
        </Grid>

        <Grid item xs={3}>
          <AppListItem
            title={translate("status")}
            subtitle={fleet.status ?? EMPTY_CHARACTER}
            disableGutters
          />
        </Grid>

        <Grid item xs={3}>
          <AppListItem
            title={translate("nickname")}
            subtitle={fleet.nickname ?? EMPTY_CHARACTER}
            disableGutters
          />
        </Grid>

        <Grid item xs={3}>
          <AppListItem
            title={translate("fleetNumber")}
            subtitle={fleet.fleetNumber ?? EMPTY_CHARACTER}
            disableGutters
          />
        </Grid>

        <Grid item xs={3}>
          <AppListItem
            title={translate("vinNumber")}
            subtitle={fleet.vinNumber ?? EMPTY_CHARACTER}
            disableGutters
          />
        </Grid>

        <Grid item xs={3}>
          <AppListItem
            title={translate("make")}
            subtitle={fleet.make ?? EMPTY_CHARACTER}
            disableGutters
          />
        </Grid>

        <Grid item xs={3}>
          <AppListItem
            title={translate("model")}
            subtitle={fleet.model ?? EMPTY_CHARACTER}
            disableGutters
          />
        </Grid>

        <Grid item xs={3}>
          <AppListItem
            title={translate("year")}
            subtitle={fleet.year > 0 ? fleet.year : EMPTY_CHARACTER}
            disableGutters
          />
        </Grid>

        <Grid item xs={3}>
          <AppListItem
            title={translate("engineType")}
            subtitle={fleet.engine ?? EMPTY_CHARACTER}
            disableGutters
          />
        </Grid>

        <Grid item xs={3}>
          <AppListItem
            title={translate("plateNumber")}
            subtitle={fleet.licensePlate ?? EMPTY_CHARACTER}
            disableGutters
          />
        </Grid>

        <Grid item xs={3}>
          <AppListItem
            title={translate("fuelType")}
            subtitle={fleet.fuelType ?? EMPTY_CHARACTER}
            disableGutters
          />
        </Grid>

        <Grid item xs={3}>
          <AppListItem
            title={translate("condition")}
            subtitle={fleet.condition ?? EMPTY_CHARACTER}
            disableGutters
          />
        </Grid>

        <Grid item xs={3}>
          <AppListItem
            title={translate("capacity")}
            subtitle={
              fleet.capacityM3 ? `${fleet.capacityM3} ${measurement?.code}` : EMPTY_CHARACTER
            }
            disableGutters
          />
        </Grid>

        <Grid item xs={3}>
          <AppListItem
            title={translate("euronorm")}
            subtitle={fleet.euronorm ?? EMPTY_CHARACTER}
            disableGutters
          />
        </Grid>

        <Grid item xs={3}>
          <AppListItem
            title={translate("driverLicense")}
            subtitle={fleet.driverLicense ?? EMPTY_CHARACTER}
            disableGutters
          />
        </Grid>

        <Grid item xs={3}>
          <AppListItem
            title={translate("manufacturer")}
            subtitle={fleet.manufacturer ?? EMPTY_CHARACTER}
            disableGutters
          />
        </Grid>

        <Grid item xs={3}>
          <AppListItem
            title={translate("branch")}
            subtitle={fleet.branches?.map((item) => item.name).join(`, `) ?? EMPTY_CHARACTER}
            disableGutters
          />
        </Grid>

        <Grid item xs={3}>
          <AppListItem
            title={translate("warehouse")}
            subtitle={fleet.warehouse?.name ?? EMPTY_CHARACTER}
            disableGutters
          />
        </Grid>

        <Grid item xs={3}>
          <AppListItem
            title={translate("team")}
            subtitle={fleet.teams?.map((item) => item.name).join(`, `) ?? EMPTY_CHARACTER}
            disableGutters
          />
        </Grid>

        <Grid item xs={3}>
          <AppListItem
            title={translate("belongsTo")}
            subtitle={fleet.ownership ?? EMPTY_CHARACTER}
            disableGutters
          />
        </Grid>

        <Grid item xs={3}>
          <AppListItem
            title={translate("subcontractor")}
            subtitle={fleet.subcontractor?.name ?? EMPTY_CHARACTER}
            disableGutters
          />
        </Grid>

        <Grid item xs={12}>
          <Checkbox
            style={{ marginTop: "16px" }}
            disabled={true}
            checked={fleet.isLoadable}
            label={translate("enableThisFleetAsAStorageBox")}
            tooltipText={translate(
              "enablingThisAllowsTheUserToChooseTheVehicleForStorageMovementOrTransshipmentAndIndicateWhenItemsArePlacedInsideLikeAStorageBox"
            )}
            tooltipPlacement={TooltipPlacements.TOP_END}
          />
        </Grid>
      </Grid>

      <Divider style={{ marginBottom: "24px", marginTop: "24px" }} />

      <Title>{translate("dates")}</Title>

      <Grid container>
        <Grid item xs={3}>
          <AppListItem
            title={translate("purchaseDate")}
            subtitle={
              fleet.purchaseDate && currentUser?.preferences
                ? formatDateWithUserPref(fleet.purchaseDate, currentUser.preferences, "dateOnly")
                : EMPTY_CHARACTER
            }
            disableGutters
          />
        </Grid>

        <Grid item xs={3}>
          <AppListItem
            title={translate("soldDate")}
            subtitle={
              fleet.soldDate && currentUser?.preferences
                ? formatDateWithUserPref(fleet.soldDate, currentUser.preferences, "dateOnly")
                : EMPTY_CHARACTER
            }
            disableGutters
          />
        </Grid>

        <Grid item xs={3}>
          <AppListItem
            title={translate("lastInspectionDate")}
            subtitle={
              fleet.lastInspectionDate && currentUser?.preferences
                ? formatDateWithUserPref(
                    fleet.lastInspectionDate,
                    currentUser.preferences,
                    "dateOnly"
                  )
                : EMPTY_CHARACTER
            }
            disableGutters
          />
        </Grid>

        <Grid item xs={3}>
          <AppListItem
            title={translate("nextInspectionDate")}
            subtitle={fleet.status ?? EMPTY_CHARACTER}
            disableGutters
          />
        </Grid>
      </Grid>
    </Container>
  );
};
